import { useState, useEffect, useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  VStack, HStack, Heading, Text, ImageCard, Grid, Badge, Skeleton, CommandBar, SearchResult, Card
} from 'wss3-forge'
import { Book20Regular, Star20Filled, Search20Regular, ThumbLike20Regular, People20Regular } from '@fluentui/react-icons'
import { getDocPlugins, searchDocs, DocPlugin, DocSearchResult } from '../lib/docs'

const FEATURED_SLUGS = ['mw-core', 'mw-inventory', 'mw-characters']

const generateStars = (count: number) => {
  return Array.from({ length: count }, (_, i) => {
    const isLeftSide = i % 2 === 0
    const baseLeft = isLeftSide ? 15 : 55
    const leftRange = isLeftSide ? 30 : 30

    return {
      id: i,
      left: baseLeft + Math.random() * leftRange,
      top: 10 + Math.random() * 70,
      delay: Math.random() * 5,
      duration: 4 + Math.random() * 4,
      size: 1 + Math.random() * 2,
      opacity: 0.3 + Math.random() * 0.4
    }
  })
}

export function DocsHome() {
  const navigate = useNavigate()
  const stars = useMemo(() => generateStars(30), [])
  const [plugins, setPlugins] = useState<DocPlugin[]>([])
  const [loading, setLoading] = useState(true)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState<SearchResult[]>([])

  useEffect(() => {
    getDocPlugins()
      .then(setPlugins)
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  const fetchResults = useCallback(async (query: string) => {
    if (!query.trim()) {
      setSearchResults([])
      return
    }
    try {
      const results = await searchDocs(query)
      setSearchResults(results.map((r: DocSearchResult) => ({
        id: r.page_id,
        type: r.result_type,
        title: r.page_title,
        subtitle: r.result_type === 'plugin'
          ? 'Plugin Documentation'
          : `${r.plugin_name} / ${r.section_title}`,
        route: r.result_type === 'plugin'
          ? `/${r.plugin_slug}`
          : `/${r.plugin_slug}/${r.section_slug}/${r.page_slug}`
      })))
    } catch {
      setSearchResults([])
    }
  }, [])

  useEffect(() => {
    const timeout = setTimeout(() => {
      fetchResults(searchQuery)
    }, 200)
    return () => clearTimeout(timeout)
  }, [searchQuery, fetchResults])

  const handleSearch = (query: string): SearchResult[] => {
    setSearchQuery(query)
    return searchResults
  }

  if (loading) {
    return (
      <div>
        {/* Hero Skeleton */}
        <div style={{
          position: 'relative',
          padding: '180px 24px 48px',
          marginBottom: 24,
          overflow: 'hidden',
          background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(246, 160, 14, 0.08), transparent)'
        }}>
          <VStack gap="lg" align="center" style={{ maxWidth: 700, margin: '0 auto' }}>
            {/* Logo */}
            <Skeleton width={140} height={48} />

            {/* Title & Subtitle */}
            <VStack gap="sm" align="center" style={{ width: '100%' }}>
              <Skeleton width={320} height={44} />
              <Skeleton width="80%" height={20} />
              <Skeleton width="60%" height={20} />
            </VStack>

            {/* Search Bar */}
            <div style={{ width: '100%', maxWidth: 520, marginTop: 16, borderRadius: 12, overflow: 'hidden' }}>
              <Skeleton width="100%" height={56} />
            </div>

            {/* Stats */}
            <HStack gap="lg" style={{ marginTop: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
              <Skeleton width={180} height={20} />
              <Skeleton width={220} height={20} />
              <Skeleton width={160} height={20} />
            </HStack>
          </VStack>
        </div>

        {/* Content Skeleton */}
        <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px 48px' }}>
          <VStack gap="xl">
            {/* Popular Section */}
            <VStack gap="md">
              <HStack gap="sm" align="center">
                <Skeleton width={20} height={20} />
                <Skeleton width={80} height={24} />
              </HStack>
              <Grid columns={{ xs: 1, sm: 2, lg: 3 }} gap="md">
                {[1, 2, 3].map(i => (
                  <VStack key={i} gap="none">
                    <div style={{ borderRadius: '12px 12px 0 0', overflow: 'hidden' }}>
                      <Skeleton height={140} />
                    </div>
                    <div style={{ padding: 16, backgroundColor: 'var(--bg-secondary)', borderRadius: '0 0 12px 12px' }}>
                      <VStack gap="sm">
                        <Skeleton width={60} height={20} />
                        <Skeleton width="70%" height={18} />
                        <Skeleton width="90%" height={14} />
                      </VStack>
                    </div>
                  </VStack>
                ))}
              </Grid>
            </VStack>

            {/* All Documentation Section */}
            <VStack gap="md">
              <Skeleton width={160} height={24} />
              <Grid columns={{ xs: 1, sm: 2, lg: 3 }} gap="md">
                {[1, 2, 3].map(i => (
                  <VStack key={i} gap="none">
                    <div style={{ borderRadius: '12px 12px 0 0', overflow: 'hidden' }}>
                      <Skeleton height={140} />
                    </div>
                    <div style={{ padding: 16, backgroundColor: 'var(--bg-secondary)', borderRadius: '0 0 12px 12px' }}>
                      <VStack gap="sm">
                        <Skeleton width={60} height={20} />
                        <Skeleton width="70%" height={18} />
                        <Skeleton width="90%" height={14} />
                      </VStack>
                    </div>
                  </VStack>
                ))}
              </Grid>
            </VStack>
          </VStack>
        </div>
      </div>
    )
  }

  const featured = plugins.filter(p => FEATURED_SLUGS.includes(p.slug))
  const others = plugins.filter(p => !FEATURED_SLUGS.includes(p.slug))

  return (
    <div>
      {/* Hero Section */}
      <div style={{
        position: 'relative',
        padding: '180px 24px 48px',
        marginBottom: 24,
        overflow: 'hidden',
        background: 'radial-gradient(ellipse 80% 50% at 50% -20%, rgba(246, 160, 14, 0.15), transparent)'
      }}>
        {/* Stars effect */}
        <div className="hero-stars">
          {stars.map((star) => (
            <div
              key={star.id}
              className="hero-star"
              style={{
                left: `${star.left}%`,
                top: `${star.top}%`,
                width: `${star.size}px`,
                height: `${star.size}px`,
                animationDelay: `${star.delay}s`,
                animationDuration: `${star.duration}s`,
                '--star-opacity': star.opacity
              } as React.CSSProperties}
            />
          ))}
        </div>


        <VStack gap="lg" align="center" style={{ position: 'relative', textAlign: 'center', maxWidth: 700, margin: '0 auto', zIndex: 2 }}>
          <img
            src="/logo_docs.png"
            alt="Murphy's Docs"
            style={{ height: 48, filter: 'drop-shadow(0 0 30px rgba(246, 160, 14, 0.3))' }}
          />
          <VStack gap="sm" align="center">
            <h1 style={{
              fontFamily: "'Kantumruy Pro', sans-serif",
              fontSize: '2.75rem',
              fontWeight: 700,
              color: 'var(--text-primary)',
              margin: 0
            }}>Murphy's Documentation</h1>
            <Text color="muted" size="md" style={{ maxWidth: 560 }}>
              Explore our comprehensive resources and get expert assistance for your premium RedM plugins. From troubleshooting to optimization, we're here to help.
            </Text>
          </VStack>

          {/* Search Bar */}
          <button
            onClick={() => setSearchOpen(true)}
            style={{
              width: '100%',
              maxWidth: 520,
              marginTop: 16,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '16px 24px',
              backgroundColor: 'var(--bg-secondary)',
              border: '1px solid var(--border-color)',
              borderRadius: 'var(--radius-lg)',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 24px rgba(0, 0, 0, 0.2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = 'var(--brand-primary)'
              e.currentTarget.style.boxShadow = '0 4px 32px rgba(246, 160, 14, 0.15)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = 'var(--border-color)'
              e.currentTarget.style.boxShadow = '0 4px 24px rgba(0, 0, 0, 0.2)'
            }}
          >
            <Search20Regular style={{ color: 'var(--text-muted)', flexShrink: 0, fontSize: 20 }} />
            <span style={{ color: 'var(--text-muted)', fontSize: 15 }}>
              Search documentation...
            </span>
            <span style={{
              marginLeft: 'auto',
              padding: '6px 10px',
              backgroundColor: 'var(--bg-tertiary)',
              borderRadius: 'var(--radius-sm)',
              fontSize: 12,
              color: 'var(--text-muted)',
              fontWeight: 500
            }}>
              ⌘K
            </span>
          </button>

          {/* Quick stats */}
          <HStack gap="lg" style={{ marginTop: 24, flexWrap: 'wrap', justifyContent: 'center' }}>
            <HStack gap="xs" style={{ alignItems: 'center' }}>
              <ThumbLike20Regular style={{ color: '#E1E4D6', width: 20, height: 20 }} />
              <Text size="sm" style={{ color: '#E1E4D6', fontSize: '14px' }}>
                Trusted by 500+ customers
              </Text>
            </HStack>
            <HStack gap="xs" style={{ alignItems: 'center' }}>
              <People20Regular style={{ color: '#E1E4D6', width: 20, height: 20 }} />
              <Text size="sm" style={{ color: '#E1E4D6', fontSize: '14px' }}>
                Supported by 1000+ discord members
              </Text>
            </HStack>
            <HStack gap="xs" style={{ alignItems: 'center' }}>
              <Star20Filled style={{ color: '#E1E4D6', width: 20, height: 20 }} />
              <Text size="sm" style={{ color: '#E1E4D6', fontSize: '14px' }}>
                Over 10 top-rated assets
              </Text>
            </HStack>
          </HStack>
        </VStack>
      </div>

      {/* Content */}
      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '0 24px 48px' }}>
        <VStack gap="xl">

        {/* Featured Plugins */}
        {featured.length > 0 && (
          <VStack gap="md">
            <HStack gap="sm" align="center">
              <Star20Filled style={{ color: 'var(--brand-primary)' }} />
              <Heading level={3}>Popular</Heading>
            </HStack>
            <Grid columns={{ xs: 1, sm: 2, lg: 3 }} gap="md">
              {featured.map(plugin => (
                <PluginCard
                  key={plugin.id}
                  plugin={plugin}
                  onClick={() => navigate(`/${plugin.slug}`)}
                />
              ))}
            </Grid>
          </VStack>
        )}

        {/* All Plugins */}
        {others.length > 0 && (
          <VStack gap="md">
            <Heading level={3}>All Documentation</Heading>
            <Grid columns={{ xs: 1, sm: 2, lg: 3 }} gap="md">
              {others.map(plugin => (
                <PluginCard
                  key={plugin.id}
                  plugin={plugin}
                  onClick={() => navigate(`/${plugin.slug}`)}
                />
              ))}
            </Grid>
          </VStack>
        )}

        {/* Empty State */}
        {plugins.length === 0 && (
          <Card padding="xl" style={{ textAlign: 'center' }}>
            <VStack gap="md" align="center">
              <Book20Regular style={{ fontSize: 48, color: 'var(--text-muted)' }} />
              <Text color="muted">No documentation available yet.</Text>
            </VStack>
          </Card>
        )}
      </VStack>
      </div>

      {/* Command Bar for Search */}
      <CommandBar
        open={searchOpen}
        onClose={() => { setSearchOpen(false); setSearchQuery(''); setSearchResults([]) }}
        placeholder="Search documentation..."
        onSearch={handleSearch}
        onResultSelect={(result) => {
          if (result.route) {
            navigate(result.route)
            setSearchOpen(false)
            setSearchQuery('')
            setSearchResults([])
          }
        }}
        isNaturalLanguage={() => false}
      />
    </div>
  )
}

function PluginCard({ plugin, onClick }: {
  plugin: DocPlugin
  onClick: () => void
}) {
  const platformVariant = plugin.platform === 'redm' ? 'error'
    : plugin.platform === 'fivem' ? 'warning'
    : 'default'

  const coverImage = plugin.linked_script?.tebex_data?.image || plugin.logo_url

  const placeholderImage = `data:image/svg+xml,${encodeURIComponent(`
    <svg width="400" height="180" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" style="stop-color:#1a1a1a"/>
          <stop offset="100%" style="stop-color:#2a2a2a"/>
        </linearGradient>
      </defs>
      <rect width="100%" height="100%" fill="url(#grad)"/>
      <text x="50%" y="50%" fill="#555" font-family="sans-serif" font-size="14" text-anchor="middle" dy=".3em">
        ${plugin.name}
      </text>
    </svg>
  `)}`

  return (
    <ImageCard
      image={coverImage || placeholderImage}
      title={plugin.name}
      description={plugin.description || undefined}
      badge={
        <Badge variant={platformVariant} size="sm">
          {plugin.platform === 'both' ? 'RedM & FiveM' : plugin.platform.toUpperCase()}
        </Badge>
      }
      onClick={onClick}
    />
  )
}
